/**
 * テナント全体の求人からの選択 (第 2 段階。画面レイアウト_SCR-M-001.md §4.6.6、Issue #782) の判定。
 * 状態を持たない関数だけを置く。
 */

// 表示する求人 (IN-M-013)
export const SCOPE_OWN = 'own'; // この求職者のために上げた求人だけ
export const SCOPE_ALL = 'all'; // おすすめも含めて全部

// 「だけ」に、同じ求職者のために他の担当者が上げた求人を含めるか (§4.6.6 #2)。
// 仮定: 含める（PM の案。第19回定期協議会 2026-11-04 で伺う。未確認）。答えで変わるのはここだけ
export const OWN_SCOPE_INCLUDES_OTHER_UPLOADERS = true;

/**
 * 求人が「この求職者のために上げた求人だけ」(IN-M-013 の own) に出るか (§4.6.6 #2)。
 *
 * @param {{target_candidate_id?: string, created_by?: string|null}} item 一覧の求人
 * @param {string} opportunityId 開いている案件の ID
 * @param {string|null|undefined} currentUserId ログイン中の利用者の ID
 * @param {boolean} [includesOtherUploaders] 他の担当者が上げた求人を含めるか。既定は上の定数
 *   (引数で渡せるのは、答えのどちらでも判定が正しいことをテストで確かめるため。画面からは渡さない)
 * @returns {boolean}
 */
export function isInOwnScope(
    item,
    opportunityId,
    currentUserId,
    includesOtherUploaders = OWN_SCOPE_INCLUDES_OTHER_UPLOADERS
) {
    if (item.target_candidate_id !== opportunityId) return false;
    if (includesOtherUploaders) return true;
    return Boolean(currentUserId) && item.created_by === currentUserId;
}

/**
 * 「おすすめ」の印 (DP-M-020) を付けるか。データベース一覧 T-01 の判定式
 * `(target_candidate_id = 開いている案件) AND (created_by = 利用者)` が偽のとき付ける (§4.6.6 #3)。
 * created_by が無い求人は偽 (印を付ける) とする。
 *
 * @param {{target_candidate_id?: string, created_by?: string|null}|null|undefined} item 一覧の求人
 * @param {string} opportunityId
 * @param {string|null|undefined} currentUserId
 * @returns {boolean} 求人が無いときは偽 (枠・取得失敗のカードには出さない)
 */
export function hasRecommendedMark(item, opportunityId, currentUserId) {
    if (!item) return false;
    const ownUploadForTarget =
        item.target_candidate_id === opportunityId
        && Boolean(currentUserId)
        && item.created_by === currentUserId;
    return !ownUploadForTarget;
}

/**
 * 表示する求人に絞る (DP-M-009)。並び (取込の新しい順) は変えない。
 *
 * @param {object[]} items テナントの求人の全件
 * @param {'own'|'all'} scope
 * @param {string} opportunityId
 * @param {string|null|undefined} currentUserId
 * @returns {object[]}
 */
export function itemsInScope(items, scope, opportunityId, currentUserId) {
    if (scope === SCOPE_ALL) return items;
    return items.filter((item) => isInOwnScope(item, opportunityId, currentUserId));
}

/**
 * 画面を表示したときの IN-M-013 (§4.6.6 #6)。選択に own に出ない求人が 1 件でもあれば all で開く
 * (own で開くと、選ばれた求人が隠れたまま実行に送られるため)。一覧に無い ID は数えない (§6.1)。
 *
 * @param {object[]} items
 * @param {Set<string>} selectedIdSet
 * @param {string} opportunityId
 * @param {string|null|undefined} currentUserId
 * @returns {'own'|'all'}
 */
export function initialScope(items, selectedIdSet, opportunityId, currentUserId) {
    const hiddenSelected = items.some(
        (item) => selectedIdSet.has(item.id) && !isInOwnScope(item, opportunityId, currentUserId)
    );
    return hiddenSelected ? SCOPE_ALL : SCOPE_OWN;
}
